import { Box, Button, Card, CardContent, Chip, CircularProgress, Grid, Stack, Typography } from "@mui/material";
import { Event as EventIcon, Article as ArticleIcon, Add as AddIcon } from '@mui/icons-material';
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { usePageMeta } from "../hooks/usePageMeta";
import { useApiCallWithAuth } from "../hooks/useApiCallWithAuth";
import { listEventsAdmin } from "../api/events";
import { adminListBlogs } from "../api/blogs";
import { EventItem } from "../types/event";
import { AdminBlogSummary } from "../types/blog";

export function HomePage() {
    usePageMeta({
        title: 'Dashboard',
        description: 'Manage Colbren Solutions events and articles.',
    });

    const { request: requestEvents } = useApiCallWithAuth(listEventsAdmin);
    const { request: requestBlogs } = useApiCallWithAuth(adminListBlogs);

    const [eventCount, setEventCount] = useState<number | null>(null);
    const [activeEventCount, setActiveEventCount] = useState<number | null>(null);
    const [blogCount, setBlogCount] = useState<number | null>(null);
    const [publishedBlogCount, setPublishedBlogCount] = useState<number | null>(null);

    useEffect(() => {
        requestEvents()
            .then((events: EventItem[]) => {
                setEventCount(events.length);
                setActiveEventCount(events.filter((e: EventItem) => e.isActive).length);
            })
            .catch(() => { setEventCount(null); setActiveEventCount(null); });

        requestBlogs()
            .then((blogs: AdminBlogSummary[]) => {
                setBlogCount(blogs.length);
                setPublishedBlogCount(blogs.filter(b => b.isPublished).length);
            })
            .catch(() => { setBlogCount(null); setPublishedBlogCount(null); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box sx={{ pt: 3 }}>
            <Typography variant='h4' sx={{ mb: 1, fontWeight: 700 }}>Dashboard</Typography>
            <Typography variant='body1' color='text.secondary' sx={{ mb: 4 }}>
                Manage your events and blog content.
            </Typography>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%', boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                        <CardContent>
                            <Stack direction='row' alignItems='center' spacing={1.5} sx={{ mb: 2 }}>
                                <EventIcon color='primary' />
                                <Typography variant='h6' sx={{ fontWeight: 600, flexGrow: 1 }}>Events</Typography>
                            </Stack>

                            {eventCount === null ? (
                                <CircularProgress size={22} sx={{ mb: 2 }} />
                            ) : (
                                <Stack direction='row' spacing={1} sx={{ mb: 2 }}>
                                    <Chip label={`${eventCount} total`} size='small' />
                                    <Chip label={`${activeEventCount} active`} size='small' color='success' variant='outlined' />
                                </Stack>
                            )}

                            <Stack direction='row' spacing={1.5}>
                                <Button component={Link} to='/events/admin' variant='contained'>
                                    Manage Events
                                </Button>
                                <Button component={Link} to='/events/new' variant='outlined' startIcon={<AddIcon />}>
                                    New Event
                                </Button>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%', boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                        <CardContent>
                            <Stack direction='row' alignItems='center' spacing={1.5} sx={{ mb: 2 }}>
                                <ArticleIcon color='primary' />
                                <Typography variant='h6' sx={{ fontWeight: 600, flexGrow: 1 }}>Articles</Typography>
                            </Stack>

                            {blogCount === null ? (
                                <CircularProgress size={22} sx={{ mb: 2 }} />
                            ) : (
                                <Stack direction='row' spacing={1} sx={{ mb: 2 }}>
                                    <Chip label={`${blogCount} total`} size='small' />
                                    <Chip label={`${publishedBlogCount} published`} size='small' color='success' variant='outlined' />
                                </Stack>
                            )}

                            <Stack direction='row' spacing={1.5}>
                                <Button component={Link} to='/blog/admin' variant='contained'>
                                    Manage Articles
                                </Button>
                                <Button component={Link} to='/blog/new' variant='outlined' startIcon={<AddIcon />}>
                                    New Post
                                </Button>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};
